import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";
import { api, fmtMoney, fileUrl } from "@/lib/api";

export default function DealSpotlight() {
  const [deals, setDeals] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/products/deals?limit=8")
      .then((r) => setDeals((r.data || []).filter((p) => (p.images || [])[0]).slice(0, 6)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (deals.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % deals.length), 6000);
    return () => clearInterval(t);
  }, [deals.length]);

  if (deals.length === 0) return null;
  const p = deals[idx];
  const save = p.compare_at_price > p.price ? p.compare_at_price - p.price : 0;

  return (
    <section className="bg-[#071330] overflow-hidden" data-testid="deal-spotlight">
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-10">
        <div className="relative bg-[#0A1E5E]/60 border border-[#F0B90B]/30 rounded-lg overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={p.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-[42%_1fr] sm:grid-cols-[300px_1fr] items-center"
            >
              <div className="relative aspect-square overflow-hidden bg-white">
                <img src={fileUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover living-img" />
              </div>
              <div className="p-5 sm:p-8 min-w-0">
                <div className="inline-flex items-center gap-1.5 text-[#F0B90B] text-[10px] font-black tracking-[0.3em] uppercase">
                  <Zap className="w-3.5 h-3.5 fill-current" /> Deal spotlight
                </div>
                <div className="font-display font-black text-white text-lg sm:text-2xl leading-tight mt-2 line-clamp-2" data-testid="spotlight-name">
                  {p.name}
                </div>
                <div className="flex items-baseline gap-3 mt-3 flex-wrap">
                  <span className="font-display font-black text-2xl sm:text-3xl text-[#F0B90B]" data-testid="spotlight-price">{fmtMoney(p.price)}</span>
                  {p.compare_at_price > p.price && (
                    <>
                      <span className="text-white/40 line-through text-sm">{fmtMoney(p.compare_at_price)}</span>
                      <span className="bg-[#DC2626] text-white text-[10px] font-black px-2 py-0.5 rounded-full">SAVE {fmtMoney(save)}</span>
                    </>
                  )}
                </div>
                <Link
                  to={`/product/${p.slug}`}
                  className="cta-sweep mt-5 inline-flex items-center gap-2 rounded-full bg-[#F0B90B] text-[#0A1E5E] font-black text-sm px-6 py-2.5 hover:-translate-y-0.5 transition-transform"
                  data-testid="spotlight-cta"
                >
                  Shop this deal <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
          {deals.length > 1 && (
            <div className="absolute bottom-3 right-4 flex gap-1.5">
              {deals.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-[width,background-color] ${i === idx ? "w-6 bg-[#F0B90B]" : "w-2.5 bg-white/30"}`}
                  aria-label={`Deal ${i + 1}`}
                  data-testid={`spotlight-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
